import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { RuleConditionType, RuleSource } from "../types/transactionRule";
import { Counterparty } from "../types/counterparty";
import { TransactionRuleDto } from "../dto/transactionRule/transaction-rule.dto";
import { TransactionRuleGroupDto } from "../dto/transactionRule/transaction-rule-group.dto";

const c = initContract();

const RuleConditionObjectSchema = z.object({
    before: z.string().optional(),
    after: z.string().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
});

const RuleCreationConditionPayloadSchema = z.object({
    conditionType: z.nativeEnum(RuleConditionType),
    value: z.union([
        z.string(),
        z.array(z.string()),
        z.number(),
        z.array(z.number()),
        RuleConditionObjectSchema,
    ]),
});

const RuleUpdateSchema = z.object({
    categoryId: z.string().uuid().nullable().optional(),
    groupId: z.string().uuid().optional(),
    entityId: z.coerce.number().nonnegative().nullable().optional(),
    classId: z.string().uuid().nullable().optional(),
    accountId: z.string().uuid().nullable().optional(),
    order: z.coerce.number().nonnegative().optional(),
    conditions: z.array(RuleCreationConditionPayloadSchema).optional(),
    isDisabled: z.boolean().optional(),
});

const RuleCreationSchema = z.object({
    rule: RuleUpdateSchema,
    source: z.nativeEnum(RuleSource),
    applyToExistingTransactions: z.boolean(),
    excludedTransactionsIds: z.array(z.number()).optional(),
});

const RuleUpdateQuerySchema = z.object({
    rule: RuleUpdateSchema,
    applyToExistingTransactions: z.boolean(),
    excludedTransactionsIds: z.array(z.number()).optional(),
});

export const ruleContract = c.router(
    {
        getRuleGroups: {
            method: "GET",
            path: "/:workspaceId",
            responses: {
                200: c.type<{
                    ruleGroups: TransactionRuleGroupDto[];
                    counterparties: Counterparty[];
                    transactionCounts: Record<string, number>;
                }>(),
            },
        },
        createRule: {
            method: "POST",
            path: "/:workspaceId/rule/",
            responses: {
                201: c.type<{ rule: TransactionRuleDto }>(),
            },
            body: RuleCreationSchema,
        },
        updateRule: {
            method: "PATCH",
            path: "/:workspaceId/rule/:ruleId",
            responses: {
                200: c.type<{ rule: TransactionRuleDto }>(),
            },
            body: RuleUpdateQuerySchema,
        },
        changeRuleOrder: {
            method: "PATCH",
            path: "/:workspaceId/rule/:ruleId/order",
            responses: {
                200: c.type<{ rule: TransactionRuleDto }>(),
            },
            body: z.object({
                order: z.number().nonnegative(),
                groupId: z.string().uuid(),
            }),
        },
        deleteRule: {
            method: "DELETE",
            path: "/:workspaceId/rule/:ruleId",
            responses: {
                200: c.noBody(),
                400: z.object({
                    reversalErrorsNumber: z.number().optional(),
                    processedTransactionsNumber: z.number().optional(),
                }),
            },
        },
        getAccountingDefaultRules: {
            method: "GET",
            path: "/:workspaceId/accounting-default-rules",
            responses: {
                200: c.type<{ ruleGroup: TransactionRuleGroupDto }>(),
            },
        },
    },
    {
        pathPrefix: "/rule-groups",
    },
);
